import { SelectOption } from '@/components/ui/Select'
import { ToggleOption } from '@/components/ui/ToggleGroup'
import { SchedulingOption } from '@/components/ui/SchedulingToggle'

/**
 * Case Constants
 *
 * This file contains all static options and constants used across case-related components.
 * Centralizing these values makes them easier to maintain and ensures consistency.
 */

// ============================================================================
// CALLER INFORMATION OPTIONS
// ============================================================================

// Call direction options
export const callDirectionOptions: ToggleOption[] = [
  {
    value: 'incoming',
    label: 'Incoming Call',
    description:
      'Select this if the case is being initiated by a received call.',
    icon: undefined, // Will be set in the component
  },
  {
    value: 'outgoing',
    label: 'Outgoing Call',
    description: 'Select this if the case begins from a call you initiated.',
    icon: undefined, // Will be set in the component
  },
]

// Gender options (shared between caller and ambulance)
export const genderOptions: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
]

// Prefix options
export const prefixOptions: SelectOption[] = [
  { value: 'mr', label: 'Mr' },
  { value: 'mrs', label: 'Mrs' },
  { value: 'miss', label: 'Miss' },
  { value: 'dr', label: 'Dr' },
  { value: 'prof', label: 'Prof' },
  { value: 'eng', label: 'Eng' },
  { value: 'other', label: 'Other' },
]

// Call type options
export const callTypeOptions: SelectOption[] = [
  { value: 'undetermined', label: 'Undetermined / Ended Without Dispatch' },
  { value: 'general_inquiry', label: 'General Inquiry' },
  { value: 'emergency', label: 'Emergency Case / Evacuation' },
  { value: 'ambulance_request', label: 'Ambulance Request' },
]

// Call category subcategories
export const undeterminedSubcategories: SelectOption[] = [
  { value: 'undetermined/accidental-call', label: 'Accidental Call' },
  { value: 'undetermined/dropped-call', label: 'Dropped Call' },
  { value: 'undetermined/irrelevant-call', label: 'Irrelevant Call' },
]

export const generalInquirySubcategories: SelectOption[] = [
  {
    value: 'general-inquiry/service-information',
    label: 'Service Information',
  },
  { value: 'general-inquiry/pricing-request', label: 'Pricing Request' },
  { value: 'general-inquiry/follow-up', label: 'Follow-up' },
]

export const emergencySubcategories: SelectOption[] = [
  {
    value: 'emergency/dispatch-first-responder',
    label: 'Dispatch ERA First Responder',
  },
]

export const ambulanceRequestSubcategories: SelectOption[] = [
  {
    value: 'ambulance-request/dispatch-era-ambulance',
    label: 'Dispatch ERA Ambulance',
  },
  {
    value: 'ambulance-request/dispatch-3rd-party-ambulance-partner',
    label: 'Dispatch 3rd Party Ambulance Partner',
  },
  {
    value: 'ambulance-request/interstate-transfer',
    label: 'Interstate Transfer',
  },
  {
    value: 'ambulance-request/interstate-hospital-transfer',
    label: 'Interstate Hospital Transfer',
  },
]

// ============================================================================
// AMBULANCE REQUEST INFORMATION OPTIONS
// ============================================================================

// Scheduling options
export const schedulingOptions: SchedulingOption[] = [
  { value: 'now', label: 'Now' },
  { value: 'schedule', label: 'Schedule' },
]

// Consciousness options
export const consciousnessOptions: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

// Oxygen requirement options
export const oxygenRequiredOptions: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

// Severity level options
export const severityLevelOptions: SelectOption[] = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
]

// LGA options (Lagos State - this would typically be dynamic based on state)
export const lgaOptions: SelectOption[] = [
  { value: 'Ikeja', label: 'Ikeja' },
  { value: 'Eti-Osa', label: 'Eti-Osa' },
  { value: 'Lagos Island', label: 'Lagos Island' },
  { value: 'Lagos Mainland', label: 'Lagos Mainland' },
  { value: 'Surulere', label: 'Surulere' },
  { value: 'Mushin', label: 'Mushin' },
  { value: 'Oshodi-Isolo', label: 'Oshodi-Isolo' },
  { value: 'Kosofe', label: 'Kosofe' },
  { value: 'Shomolu', label: 'Shomolu' },
  { value: 'Ajeromi-Ifelodun', label: 'Ajeromi-Ifelodun' },
  { value: 'Amuwo-Odofin', label: 'Amuwo-Odofin' },
  { value: 'Apapa', label: 'Apapa' },
  { value: 'Badagry', label: 'Badagry' },
  { value: 'Ibeju-Lekki', label: 'Ibeju-Lekki' },
  { value: 'Ifako-Ijaiye', label: 'Ifako-Ijaiye' },
  { value: 'Ikorodu', label: 'Ikorodu' },
  { value: 'Ojo', label: 'Ojo' },
  { value: 'Alimosho', label: 'Alimosho' },
  { value: 'Agege', label: 'Agege' },
]

// Provisional diagnosis options
export const provisionalDiagnosisOptions: SelectOption[] = [
  { value: 'Others', label: 'Others' },
  {
    value: 'Cardiac or Respiratory Arrest/Death',
    label: 'Cardiac or Respiratory Arrest/Death',
  },
  {
    value: 'Stroke (CVA)/Transient Ischemic Attack',
    label: 'Stroke (CVA)/Transient Ischemic Attack',
  },
  { value: 'Infectious Disease', label: 'Infectious Disease' },
  { value: 'Convulsions/Seizures', label: 'Convulsions/Seizures' },
  {
    value: 'Unconscious/Fainting (Near)',
    label: 'Unconscious/Fainting (Near)',
  },
  { value: 'Animal Bites/Attacks', label: 'Animal Bites/Attacks' },
  {
    value: 'Assault/Sexual Assault/Stun Gun',
    label: 'Assault/Sexual Assault/Stun Gun',
  },
  { value: 'Bleeding/Laceration', label: 'Bleeding/Laceration' },
  {
    value: 'Burns (Scalds)/Explosion (Blast)',
    label: 'Burns (Scalds)/Explosion (Blast)',
  },
  { value: 'Eye Problems/Injuries', label: 'Eye Problems/Injuries' },
  { value: 'Falls', label: 'Falls' },
  { value: 'Industrial Accidents', label: 'Industrial Accidents' },
  { value: 'Stabbing/Gunshot/Assault', label: 'Stabbing/Gunshot/Assault' },
  { value: 'Abdominal Pain/Problems', label: 'Abdominal Pain/Problems' },
  {
    value: 'Allergies (Reactions)/Envenomations (Stings, Bites)',
    label: 'Allergies (Reactions)/Envenomations (Stings, Bites)',
  },
  {
    value: 'Back Pain (Non-Traumatic Or Non-Recent Trauma)',
    label: 'Back Pain (Non-Traumatic Or Non-Recent Trauma)',
  },
  { value: 'Breathing Problems', label: 'Breathing Problems' },
  {
    value: 'Chest Pain/Chest Discomfort (Non-Traumatic)',
    label: 'Chest Pain/Chest Discomfort (Non-Traumatic)',
  },
  { value: 'Diabetic Problems', label: 'Diabetic Problems' },
  { value: 'Headache', label: 'Headache' },
  {
    value: 'Poisoning/substance Abuse/Ingestions',
    label: 'Poisoning/substance Abuse/Ingestions',
  },
  {
    value: 'Psychiatric/Abnormal Behavioral/Suicide Attempt',
    label: 'Psychiatric/Abnormal Behavioral/Suicide Attempt',
  },
  {
    value: 'Unknown Problem (Person down)',
    label: 'Unknown Problem (Person down)',
  },
  {
    value: 'Carbon Monoxide/Inhalation/HAZMAT/CBRN',
    label: 'Carbon Monoxide/Inhalation/HAZMAT/CBRN',
  },
  {
    value: 'Drowning/Near Drowning/Diving/SCUBA Accident',
    label: 'Drowning/Near Drowning/Diving/SCUBA Accident',
  },
  { value: 'Electrocution/Lightning', label: 'Electrocution/Lightning' },
  {
    value: 'Pregnancy/Childbirth/Miscarriage',
    label: 'Pregnancy/Childbirth/Miscarriage',
  },
  { value: 'Haemorrhage/Laceration', label: 'Haemorrhage/Laceration' },
  { value: 'Choking', label: 'Choking' },
  { value: 'Heart Problems', label: 'Heart Problems' },
  { value: 'Heat/Cold Exposure', label: 'Heat/Cold Exposure' },
  { value: 'Inaccessible Incident', label: 'Inaccessible Incident' },
  {
    value: 'Move A Patient Between Hospitals',
    label: 'Move A Patient Between Hospitals',
  },
  {
    value: 'Overdose/Poisoning (Indigestion)',
    label: 'Overdose/Poisoning (Indigestion)',
  },
  { value: 'Pandemic/Epidemic/Endemic', label: 'Pandemic/Epidemic/Endemic' },
  { value: 'Sick Person', label: 'Sick Person' },
  { value: 'Stab/Gunshot', label: 'Stab/Gunshot' },
  {
    value: 'Traffic/Transportation Incidents',
    label: 'Traffic/Transportation Incidents',
  },
  {
    value: 'Transfer/Interfacility/Palliative care',
    label: 'Transfer/Interfacility/Palliative care',
  },
  { value: 'Traumatic Injuries', label: 'Traumatic Injuries' },
  { value: 'Vehicle Breakdown', label: 'Vehicle Breakdown' },
]

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get subcategories for a specific call type
 * @param callType - The call type to get subcategories for
 * @returns Array of subcategory options
 */
export const getSubcategoriesForCallType = (
  callType: string
): SelectOption[] => {
  switch (callType) {
    case 'undetermined':
      return undeterminedSubcategories
    case 'general_inquiry':
      return generalInquirySubcategories
    case 'emergency':
      return emergencySubcategories
    case 'ambulance_request':
      return ambulanceRequestSubcategories
    default:
      return []
  }
}

/**
 * Get all call category options (flattened)
 * @returns Array of all call category options
 */
export const getAllCallCategoryOptions = (): SelectOption[] => {
  return [
    ...undeterminedSubcategories,
    ...generalInquirySubcategories,
    ...emergencySubcategories,
    ...ambulanceRequestSubcategories,
  ]
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

// Re-export with old names for backward compatibility
export const defaultCallTypeOptions = callTypeOptions
export { callDirectionOptions as callDirectionToggleOptions }
